// src/auth/fetchMiddleware.js
// Глобальный перехватчик window.fetch, который "прокатывает" все запросы через нашу логику.
// Важно: используем сохранённый originalFetch, чтобы не уйти в рекурсию.

import { getAccess, getRefresh, clearTokens } from './tokenStorage';
import { refreshAccessToken, setOnLogout } from './tokenManager';

const AUTH_ENDPOINTS = [
  '/api/sign-in/',
  '/api/register/',
  '/api/token/refresh/',
];

function isApiRequest(urlLike) {
  // Перехватываем только ваши API-запросы (начинаются с /api/)
  try {
    const url = typeof urlLike === 'string' ? urlLike : urlLike?.url;
    return typeof url === 'string' && url.startsWith('/api/');
  } catch {
    return false;
  }
}

function isAuthEndpoint(urlLike) {
  // Логин/регистрация/refresh не перехватываются
  const url = typeof urlLike === 'string' ? urlLike : urlLike?.url || '';
  return AUTH_ENDPOINTS.some((ep) => url.includes(ep));
}

function mergeHeaders(orig, patch) {
  const h = new Headers(orig || {});
  Object.entries(patch || {}).forEach(([k, v]) => {
    if (typeof v !== 'undefined' && v !== null) h.set(k, v);
  });
  return h;
}

export function installFetchMiddleware({ loginPath = '/sign-in' } = {}) {
  if (typeof window === 'undefined' || !window.fetch) return;
  if (window.__fetchMiddlewareInstalled) return;
  window.__fetchMiddlewareInstalled = true;

  const baseFetch = window.fetch.bind(window);

  // Настройка поведения logout по умолчанию
  setOnLogout(() => {
    clearTokens();
    if (typeof window !== 'undefined') {
      window.location.href = loginPath;
    }
  });

  window.fetch = async (input, init = {}) => {
    const api = isApiRequest(input);
    const authEp = isAuthEndpoint(input);
    const shouldIntercept = api && !authEp;

    // Заголовки, переданные вызывающим кодом
    const baseHeaders =
      init?.headers ||
      (input instanceof Request ? input.headers : undefined) ||
      undefined;

    let headers = new Headers(baseHeaders);

    // PRE-REFRESH: если это API-запрос и нет Authorization, попробуем получить access заранее
    if (shouldIntercept && !headers.has('Authorization')) {
      let access = getAccess();
      if (!access && getRefresh()) {
        try {
          await refreshAccessToken(); // внутри есть single-flight
          access = getAccess();
        } catch {
          // refresh провалился -> onLogout уже вызван; дадим запросу идти как есть (вернётся 401)
        }
      }
      if (access) {
        headers.set('Authorization', `Bearer ${access}`);
      }
    }

    // Первый проход: отправляем запрос
    const firstInit = { ...init, headers };
    let response = await baseFetch(input, firstInit);

    // Если это не перехватываемый сценарий или не 401 — возвращаем как есть
    if (!shouldIntercept || response.status !== 401) {
      return response;
    }

    // 401: делаем refresh и один ретрай
    try {
      await refreshAccessToken();
    } catch {
      // refresh не удался — onLogout уже произошёл; вернём исходный 401
      return response;
    }

    // Повторяем запрос с обновлённым access
    const retryHeaders = mergeHeaders(headers, {
      Authorization: `Bearer ${getAccess()}`,
    });
    const retryInit = { ...init, headers: retryHeaders };

    return baseFetch(input, retryInit);
  };
}

// Автоустановка при импорте
installFetchMiddleware();
